import axios from 'axios'
import router from '../router'
import store from '../store'

// The authenticated half of the API. Everything except the public namespace
// lives behind the auth plug, so this is the client that carries the token, and
// `backend.ts` is the one that stays out of the way of browsing.
//
// `Bearer` because that is the one form `Metalist.AuthPlug` extracts a token
// from, and a header in any other shape is read as no token at all -- so a
// mismatch here would not fail loudly, it would fail as a redirect to sign-in on
// every request.
export const client = axios.create({
  baseURL: '/api/',
  timeout: 5000,
  headers: { 'Content-Type': 'application/json' }
})

// The token comes from the store rather than from local storage directly,
// because the store is built from local storage on load and the two cannot
// disagree. It sent a placeholder here before, which meant a real token would
// have been silently discarded the moment one existed.
client.interceptors.request.use(function (config) {
  const token = store.state.token

  if (token !== null) {
    config.headers['Authorization'] = 'Bearer ' + token
  }

  return config
})

// A `401` here is the server saying the token is no longer good: expired,
// revoked by a sign-out or a password change, or issued to an account that is
// gone or disabled. All of those are answered the same way and all of them mean
// the same thing to the browser -- there is no session any more, so the session
// is dropped and the visitor is asked to sign in again. That is what makes an
// expired session a prompt rather than an error page, and it is why nothing is
// reported to the visitor beyond being sent to the form: the four reasons are
// deliberately indistinguishable to a client, and telling them apart here would
// tell an attacker apart too.
//
// `replace` and not `push`, so that the back button does not return to a page
// that needs a session and bounce straight back here.
client.interceptors.response.use(function (response) {
  return response
}, function (error) {
  if (error.response && error.response.status === 401) {
    store.commit('sessionCleared')

    // Already where this would send them. A refused sign-in lands here too --
    // the request is an authenticated one, and it is answered with a `401` --
    // and asking for the route already shown is a navigation Vue Router rejects.
    if (router.currentRoute.name !== 'signin') {
      router.replace({ name: 'signin' })
    }
  }

  return Promise.reject(error)
})
